import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, Search } from 'lucide-react';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import { listarIndicacoes } from '../../../src/services/indicacoes';
import { ANDAMENTO, ROTULO_MOTIVO, formatarDataHora } from '../../../src/lib/indicacoes/rotulos';
import { formatarTelefoneBr } from '../../../src/lib/enderecoUc';
import type { IndicacaoAndamento, IndicacaoItem } from '../../../types';
import NegotiationDetailModal from '../components/NegotiationDetails/NegotiationDetailModal';
import Selo from './Selo';
import { CLASSE_BOTAO_SECUNDARIO, CLASSE_CARTAO, CLASSE_INPUT } from './estilos';

const POR_PAGINA = 50;
const ANDAMENTOS = Object.keys(ANDAMENTO) as IndicacaoAndamento[];

const IndicacoesTab: React.FC<{ versao: number }> = ({ versao }) => {
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const [andamento, setAndamento] = useState<IndicacaoAndamento | ''>('');
  const [pagina, setPagina] = useState(0);
  const [linhas, setLinhas] = useState<IndicacaoItem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [negAberta, setNegAberta] = useState<number | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setBuscaAplicada(busca.trim());
      setPagina(0);
    }, 350);
    return () => clearTimeout(t);
  }, [busca]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setLinhas(await listarIndicacoes({ busca: buscaAplicada, andamento, limite: POR_PAGINA, offset: pagina * POR_PAGINA }));
    } catch (e) {
      setErro((e as Error).message);
      setLinhas([]);
    } finally {
      setCarregando(false);
    }
  }, [buscaAplicada, andamento, pagina]);

  useEffect(() => {
    carregar();
  }, [carregar, versao]);

  const total = linhas[0]?.total ?? 0;
  const ultimaPagina = Math.max(0, Math.ceil(total / POR_PAGINA) - 1);

  return (
    <div className="space-y-3">
      <div className={`${CLASSE_CARTAO} p-3 flex flex-col sm:flex-row gap-2`}>
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className={`${CLASSE_INPUT} pl-9`}
            placeholder="Buscar por indicado, quem indicou ou WhatsApp"
            value={busca}
            onChange={e => setBusca(e.target.value)}
          />
        </div>
        <select
          className={`${CLASSE_INPUT} sm:w-60`}
          value={andamento}
          onChange={e => {
            setAndamento(e.target.value as IndicacaoAndamento | '');
            setPagina(0);
          }}
        >
          <option value="">Todos os andamentos</option>
          {ANDAMENTOS.map(a => (
            <option key={a} value={a}>{ANDAMENTO[a].rotulo}</option>
          ))}
        </select>
      </div>

      {erro && <p className="text-sm text-red-600">{erro}</p>}

      <div className={`${CLASSE_CARTAO} overflow-x-auto`}>
        {carregando ? (
          <LoadingSpinner label="Carregando indicações..." />
        ) : linhas.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-slate-400 text-center py-12">Nenhuma indicação encontrada.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 dark:text-slate-400 border-b border-gray-100 dark:border-slate-700">
              <tr>
                <th className="text-left font-semibold px-4 py-2">Registrada</th>
                <th className="text-left font-semibold px-4 py-2">Indicado</th>
                <th className="text-left font-semibold px-4 py-2">Quem indicou</th>
                <th className="text-left font-semibold px-4 py-2">Comercial</th>
                <th className="text-left font-semibold px-4 py-2">Andamento</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700/60">
              {linhas.map(l => {
                const est = ANDAMENTO[l.andamento];
                const negId = l.negociacao_id;
                return (
                  <tr key={l.id} className="hover:bg-gray-50/70 dark:hover:bg-slate-800/40">
                    <td className="px-4 py-2.5 text-xs">
                      <p className="text-gray-700 dark:text-slate-200">{formatarDataHora(l.registrado_em)}</p>
                      <p className="text-gray-500 dark:text-slate-400">
                        {l.registrado_por_nome ?? '—'}
                        {l.forma === 'espontanea' ? ' · espontânea' : ''}
                      </p>
                    </td>
                    <td className="px-4 py-2.5">
                      <p className="font-semibold text-gray-900 dark:text-white">{l.indicado_nome}</p>
                      <p className="text-xs text-gray-500 dark:text-slate-400">
                        {[l.indicado_whatsapp ? formatarTelefoneBr(l.indicado_whatsapp) : null, l.indicado_cidade].filter(Boolean).join(' · ')}
                      </p>
                    </td>
                    <td className="px-4 py-2.5 text-gray-700 dark:text-slate-200">{l.indicador_nome}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-700 dark:text-slate-200">
                      {l.responsavel_nome ?? '—'}
                      {l.destino === 'rodizio' ? <span className="text-gray-500 dark:text-slate-400"> (rodízio)</span> : null}
                    </td>
                    <td className="px-4 py-2.5">
                      <Selo classe={est.classe}>{est.rotulo}</Selo>
                      {!l.vale && l.motivo_nao_vale && (
                        <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-0.5">{ROTULO_MOTIVO[l.motivo_nao_vale]}</p>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {negId != null && (
                        <button onClick={() => setNegAberta(negId)} className="p-1 text-gray-400 hover:text-primary" title="Abrir negociação">
                          <ExternalLink size={15} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {total > POR_PAGINA && (
        <div className="flex items-center justify-end gap-2 text-xs text-gray-600 dark:text-slate-300">
          <span>Página {pagina + 1} de {ultimaPagina + 1} · {total} indicações</span>
          <button disabled={pagina === 0} onClick={() => setPagina(p => p - 1)} className={CLASSE_BOTAO_SECUNDARIO}>
            <ChevronLeft size={14} />
          </button>
          <button disabled={pagina >= ultimaPagina} onClick={() => setPagina(p => p + 1)} className={CLASSE_BOTAO_SECUNDARIO}>
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      <NegotiationDetailModal
        negotiationId={negAberta}
        isOpen={negAberta != null}
        onClose={() => setNegAberta(null)}
        onUpdate={carregar}
      />
    </div>
  );
};

export default IndicacoesTab;
